import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, GripVertical, X, Save, Video, FileText, Download, HelpCircle, Package, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import CohortsManager from "./CohortsManager";

/**
 * LessonsManager — inline admin editor for a single course product's lesson list.
 * Rendered inside ProductAdminCard when the product is type=course.
 */
export default function LessonsManager({ productId, onClose }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // lesson doc or a new blank
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lms/${productId}/lessons`);
      setLessons(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [productId]);

  const openNew = () => setEditing({
    _new: true, title: "", content_type: "markdown", content: "", video_url: "", download_url: "",
    captions_url: "", unlock_days_after: 0, est_minutes: 10,
    quiz: { questions: [], pass_threshold: 0.8 }, pass_threshold: 0.8,
  });

  const save = async (form) => {
    try {
      if (form._new) {
        const { _new, ...payload } = form;
        await api.post(`/admin/lms/${productId}/lessons`, payload);
      } else {
        await api.patch(`/admin/lms/lessons/${form.id}`, form);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson? Learner progress on it will also be removed.")) return;
    try {
      await api.delete(`/admin/lms/lessons/${id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const iconFor = (t) =>
    t === "video" ? <Video size={12} />
    : t === "download" ? <Download size={12} />
    : t === "quiz" ? <HelpCircle size={12} />
    : t === "scorm" ? <Package size={12} />
    : <FileText size={12} />;

  return (
    <div className="bg-[var(--cream)] border-t border-[var(--line)] p-5 space-y-3" data-testid="lessons-manager">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--muted)]">Course Lessons</div>
          <div className="font-display text-lg text-[var(--navy)]">
            {loading ? "Loading…" : `${lessons.length} lesson${lessons.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={openNew} className="btn btn-navy !py-1.5 !px-3 !text-[11px]" data-testid="lesson-add">
            <Plus size={12} className="mr-1" /> Add Lesson
          </button>
          {onClose && (
            <button onClick={onClose} className="btn btn-ghost !py-1.5 !px-3 !text-[11px]"><X size={12} /></button>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2">{error}</div>}

      {!loading && lessons.length === 0 && !editing && (
        <div className="text-xs text-[var(--muted)] py-3">No lessons yet. Click "Add Lesson" to start building the curriculum.</div>
      )}

      <div className="space-y-2">
        {lessons.map((l, i) => (
          <div key={l.id} className="flex items-center gap-3 bg-white border border-[var(--line)] px-3 py-2" data-testid={`lesson-row-${l.id}`}>
            <GripVertical size={14} className="text-[var(--muted)]" />
            <span className="text-[10px] font-bold text-[var(--gold)] w-6">#{i + 1}</span>
            <span className="text-[var(--muted)]">{iconFor(l.content_type)}</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[var(--navy)] text-sm truncate">{l.title}</div>
              <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
                {l.content_type} {l.est_minutes ? `· ~${l.est_minutes} min` : ""}
                {l.content_type === "scorm" && l.scorm_uploaded_at ? " · uploaded" : ""}
              </div>
            </div>
            {l.content_type === "scorm" && (
              <ScormUploader lessonId={l.id} onDone={load} />
            )}
            <button onClick={() => setEditing(l)} className="p-1.5 hover:bg-[var(--cream)]" data-testid={`lesson-edit-${l.id}`}>
              <Pencil size={13} />
            </button>
            <button onClick={() => remove(l.id)} className="p-1.5 hover:bg-red-50 text-red-700" data-testid={`lesson-delete-${l.id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      {editing && <LessonEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} />}

      {/* Cohorts */}
      <div className="pt-4">
        <CohortsManager productId={productId} />
      </div>
    </div>
  );
}

function ScormUploader({ lessonId, onDone }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  const upload = async (file) => {
    setUploading(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/lms/lessons/${lessonId}/scorm-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setMsg(`✓ ${data.uploaded} files · entry: ${data.entry}`);
      if (onDone) onDone();
    } catch (e) {
      setMsg(`✗ ${formatApiError(e)}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-1" title="Upload SCORM 1.2 zip">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="p-1.5 hover:bg-[var(--cream)]"
        data-testid={`scorm-upload-${lessonId}`}
        disabled={uploading}
      >
        {uploading ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
      />
      {msg && <span className="text-[10px] text-[var(--muted)] max-w-[160px] truncate">{msg}</span>}
    </div>
  );
}

function LessonEditor({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[var(--navy)] p-4 space-y-3" data-testid="lesson-editor">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--gold)]">
        {form._new ? "New Lesson" : "Edit Lesson"}
      </div>
      <div>
        <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Title</label>
        <input required value={form.title} onChange={(e) => set("title", e.target.value)}
          className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none"
          data-testid="lesson-title" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Type</label>
          <select value={form.content_type} onChange={(e) => set("content_type", e.target.value)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" data-testid="lesson-type">
            <option value="markdown">Reading (markdown)</option>
            <option value="video">Video (URL)</option>
            <option value="download">Downloadable</option>
            <option value="quiz">Quiz</option>
            <option value="scorm">SCORM 1.2</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Est. minutes</label>
          <input type="number" min={1} value={form.est_minutes || ""} onChange={(e) => set("est_minutes", parseInt(e.target.value) || null)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" />
        </div>
      </div>
      {form.content_type === "markdown" && (
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Content (Markdown)</label>
          <textarea rows={8} value={form.content} onChange={(e) => set("content", e.target.value)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm font-mono leading-relaxed"
            placeholder="# Lesson heading&#10;&#10;Write your lesson body in markdown…"
            data-testid="lesson-content" />
        </div>
      )}
      {form.content_type === "video" && (
        <>
          <div>
            <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Video URL</label>
            <input type="url" value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)}
              placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
              className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" data-testid="lesson-video" />
            <p className="text-[10px] text-[var(--muted)] mt-1">YouTube, Vimeo, Loom, or direct MP4 URL.</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Captions URL (.vtt) <span className="text-[var(--muted)] normal-case font-normal">(optional)</span></label>
            <input type="url" value={form.captions_url || ""} onChange={(e) => set("captions_url", e.target.value)}
              placeholder="https://…/captions.vtt"
              className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" data-testid="lesson-captions" />
            <p className="text-[10px] text-[var(--muted)] mt-1">WebVTT file. Ignored for YouTube/Vimeo embeds (host their own).</p>
          </div>
        </>
      )}
      <div>
        <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Drip unlock <span className="text-[var(--muted)] normal-case font-normal">(days after learner starts the course — 0 = immediate)</span></label>
        <input type="number" min={0} max={365} value={form.unlock_days_after ?? 0}
          onChange={(e) => set("unlock_days_after", parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" data-testid="lesson-drip" />
      </div>
      {form.content_type === "download" && (
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1 uppercase">Download URL</label>
          <input type="url" value={form.download_url || ""} onChange={(e) => set("download_url", e.target.value)}
            placeholder="https://…"
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm" />
        </div>
      )}
      {form.content_type === "quiz" && (
        <QuizEditor
          quiz={form.quiz || { questions: [], pass_threshold: 0.8 }}
          onChange={(quiz) => set("quiz", quiz)}
        />
      )}
      {form.content_type === "scorm" && (
        <div className="bg-[var(--cream)] border-l-4 border-[var(--gold)] p-3 text-xs text-[var(--ink)]">
          <div className="font-bold text-[var(--navy)] mb-1">SCORM 1.2 package</div>
          Save this lesson first, then use the <strong>Upload</strong> icon next to the lesson row to attach the SCORM zip
          (must contain <code className="text-[var(--navy)]">imsmanifest.xml</code> at the root).
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn btn-ghost !py-1.5 !px-3 !text-[11px]">Cancel</button>
        <button type="submit" disabled={saving} className="btn btn-navy !py-1.5 !px-3 !text-[11px] disabled:opacity-60" data-testid="lesson-save">
          {saving ? <><Loader2 className="animate-spin mr-1" size={12} /> Saving…</> : <><Save size={12} className="mr-1" /> Save Lesson</>}
        </button>
      </div>
    </form>
  );
}

function QuizEditor({ quiz, onChange }) {
  const questions = quiz.questions || [];
  const thresh = quiz.pass_threshold ?? 0.8;

  const setQuestions = (next) => onChange({ ...quiz, questions: next });
  const setThresh = (t) => onChange({ ...quiz, pass_threshold: t });

  const addQuestion = () => setQuestions([...questions, {
    q: "", options: ["", ""], correct: 0, explanation: "",
  }]);

  const updateQ = (idx, patch) => {
    const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    setQuestions(next);
  };

  const updateOpt = (qi, oi, val) => {
    const q = questions[qi];
    const opts = q.options.map((o, i) => (i === oi ? val : o));
    updateQ(qi, { options: opts });
  };

  const addOption = (qi) => {
    const q = questions[qi];
    updateQ(qi, { options: [...q.options, ""] });
  };

  const removeOption = (qi, oi) => {
    const q = questions[qi];
    const opts = q.options.filter((_, i) => i !== oi);
    let correct = q.correct;
    if (correct === oi) correct = 0;
    else if (correct > oi) correct -= 1;
    updateQ(qi, { options: opts, correct });
  };

  const removeQuestion = (qi) => setQuestions(questions.filter((_, i) => i !== qi));

  return (
    <div className="bg-[var(--cream)] border border-[var(--line)] p-4 space-y-4" data-testid="quiz-editor">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--gold)]">Quiz Builder</div>
        <div className="flex items-center gap-2 text-xs">
          <label className="text-[var(--muted)]">Pass %</label>
          <input
            type="number" min={0} max={100}
            value={Math.round(thresh * 100)}
            onChange={(e) => setThresh(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) / 100)}
            className="w-16 px-2 py-1 border border-[var(--line)] text-sm text-right"
            data-testid="quiz-threshold"
          />
        </div>
      </div>

      {questions.length === 0 && (
        <div className="text-xs text-[var(--muted)] italic">No questions yet. Add one to start.</div>
      )}

      {questions.map((q, qi) => (
        <div key={qi} className="bg-white border border-[var(--line)] p-3 space-y-2" data-testid={`qedit-${qi}`}>
          <div className="flex items-start gap-2">
            <span className="text-[10px] font-bold text-[var(--gold)] pt-2">Q{qi + 1}</span>
            <textarea
              rows={2}
              value={q.q}
              onChange={(e) => updateQ(qi, { q: e.target.value })}
              placeholder="Question text"
              className="flex-1 px-2 py-1.5 border border-[var(--line)] text-sm"
              data-testid={`qedit-${qi}-text`}
            />
            <button
              type="button"
              onClick={() => removeQuestion(qi)}
              className="p-1 hover:bg-red-50 text-red-700 mt-1"
              title="Remove question"
            >
              <Trash2 size={12} />
            </button>
          </div>
          <div className="space-y-1 ml-6">
            {q.options.map((o, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`correct-${qi}`}
                  checked={q.correct === oi}
                  onChange={() => updateQ(qi, { correct: oi })}
                  data-testid={`qedit-${qi}-correct-${oi}`}
                />
                <input
                  value={o}
                  onChange={(e) => updateOpt(qi, oi, e.target.value)}
                  placeholder={`Option ${oi + 1}`}
                  className="flex-1 px-2 py-1 border border-[var(--line)] text-sm"
                />
                {q.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(qi, oi)}
                    className="p-1 hover:bg-red-50 text-red-700"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addOption(qi)}
              className="text-[11px] text-[var(--navy)] font-bold hover:underline"
            >
              + Add option
            </button>
          </div>
          <div className="ml-6">
            <input
              value={q.explanation || ""}
              onChange={(e) => updateQ(qi, { explanation: e.target.value })}
              placeholder="Explanation (shown after submit)"
              className="w-full px-2 py-1 border border-[var(--line)] text-xs italic text-[var(--muted)]"
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addQuestion}
        className="btn btn-ghost !py-1 !px-3 !text-[11px]"
        data-testid="quiz-add-question"
      >
        <Plus size={12} className="mr-1" /> Add question
      </button>
    </div>
  );
}

